import { create } from "zustand";
import {
  compareVersions,
  ERROR_MAINTENANCE,
  ERROR_UPGRADE_REQUIRED,
  type RemoteConfigResponse,
} from "@charter/shared";
import { BUILD } from "@/config/build";
import { followServerBuild } from "./buildFollow";
import { track } from "./telemetry";

/**
 * The release-safety wall. Two sources set it: the remote config fetched at boot, and any API
 * response answering 426 (upgrade required) or 503 with code `maintenance`. Once set, `GateScreen`
 * covers the game; the gate never clears on its own, only a fresh boot re-evaluates it.
 */
type GateState =
  | { kind: "open" }
  | { kind: "update_required"; minVersion: string }
  | { kind: "maintenance"; message?: string };

interface GateStore {
  gate: GateState;
  set: (gate: GateState) => void;
}

const useGateStore = create<GateStore>((set) => ({
  gate: { kind: "open" },
  set: (gate) => set({ gate }),
}));

function raise(gate: Exclude<GateState, { kind: "open" }>): void {
  const current = useGateStore.getState().gate;
  if (current.kind === gate.kind) return;
  useGateStore.getState().set(gate);
  track("gate.raised", { kind: gate.kind });
}

export function applyGateFromConfig(config: RemoteConfigResponse): void {
  if (config.maintenance.active) {
    raise({
      kind: "maintenance",
      ...(config.maintenance.message ? { message: config.maintenance.message } : {}),
    });
    return;
  }
  if (compareVersions(BUILD.version, config.minClientVersion) < 0) {
    raise({ kind: "update_required", minVersion: config.minClientVersion });
    return;
  }
  // same-build mode: a web client on another build reloads into the server's (buildFollow.ts)
  followServerBuild(config.build, { v: config.protocol, min: config.minProtocol });
}

/** Called by apiRequest for every non-2xx so a mid-session kill switch takes effect at once. */
export function applyGateFromError(status: number, code: string, message: string): void {
  if (status === 426 || code === ERROR_UPGRADE_REQUIRED) {
    raise({ kind: "update_required", minVersion: "" });
  } else if (status === 503 && code === ERROR_MAINTENANCE) {
    raise({ kind: "maintenance", ...(message ? { message } : {}) });
  }
}
